/**
 * Character Visibility Settings - Per-Field Privacy Control
 *
 * File: Mythbound.App/src/components/campaign/CharacterVisibilitySettings.tsx
 * Created: 2026-01-06
 * Last Modified: 2026-01-06
 *
 * PURPOSE:
 * Modal for per-field character visibility checkboxes.
 * Allows the character's owner to control which stats are visible to the
 * campaign party. CORRECTED 001A.4: the GM does NOT see hidden fields —
 * that bypass was removed backend-side in 001A.3/001A.4; the text below
 * previously said otherwise and was wrong (see AUTHORIZATION further down).
 *
 * UI LAYOUT:
 * ┌────────────────────────────┐
 * │ Character Visibility       │
 * ├────────────────────────────┤
 * │ Gandalf (Your Character)   │
 * │                            │
 * │ [✓] Hit Points             │
 * │ [✓] Armor Class            │
 * │ [✓] Speed                  │
 * │ [ ] Strength               │
 * │ [✓] Dexterity              │
 * │ [✓] Constitution           │
 * │ [✓] Intelligence           │
 * │ [✓] Wisdom                 │
 * │ [ ] Charisma               │
 * │ [✓] Skills                 │
 * │ [✓] Spells                 │
 * │ [✓] Equipment              │
 * │                            │
 * │ Note: hidden fields are    │
 * │ hidden from the GM too     │
 * │                            │
 * │ [Save] [Cancel]            │
 * └────────────────────────────┘
 *
 * KEY FEATURES:
 * - List of all character fields with checkboxes
 * - Checked = "visible to other campaign members" (backend default when a
 *   field is never explicitly set — an omitted key defaults to checked here too)
 * - Unchecked = "hidden from everyone except the owner"
 * - Owner can see and modify; a non-owner viewer sees a read-only view
 * - Save button calls characterService.updateCharacterVisibility directly
 * - Toast on success: "Visibility settings updated"
 *
 * VISUAL DESIGN:
 * - Clean tech UI (functional element)
 * - Dark modal background
 * - Gold checkboxes for checked items
 * - Informational note about GM visibility
 *
 * AUTHORIZATION (PRD001A 001A.4 — matches the frozen backend contract
 * exactly: PUT /{id}/visibility is owner-only, no GM bypass):
 * - Only the character owner can modify visibility — gated by `isOwner`
 *   below, NOT `isGM`. These are different things: a GM who also owns THIS
 *   character must still be able to edit it; a non-GM who does not own it
 *   must not. The only place this modal is opened today
 *   (`CharacterRosterSidebar.tsx`'s "👁️ Visibility" button) only ever does
 *   so for the viewer's OWN character, so `isOwner` defaults to `true` —
 *   existing callers that don't pass it keep working correctly.
 * - `isGM` (unchanged prop) only affects the informational "(Viewing as GM)"
 *   label text now — it previously also drove the read-only gate, which was
 *   a real bug: a GM viewing their OWN character would have been
 *   incorrectly locked out of editing it.
 *
 * WHO: Opened by `CharacterRosterSidebar.tsx`, for the viewer's own character only.
 * WHAT: Calls `characterService.updateCharacterVisibility` DIRECTLY — this
 * component does NOT go through `useCharacter()`/`CharacterContext`. The
 * header comment here previously claimed a "CharacterContext.updateVisibility"
 * pipeline that was never true (no `CharacterContext` import ever existed in
 * this file — the same stale-comment pattern the 001A.1 inventory flagged in
 * `CharacterCreationModal.tsx`/`CharacterRosterSidebar.tsx`); corrected below.
 *
 * Pipeline: UI → characterService.updateCharacterVisibility → Backend (no context involved)
 * Dependencies: React, useState, characterService
 */

import React, { useState, useEffect } from 'react';
import { characterService, type Character } from '../../services/api/CharacterService';
import { toastService } from '../../services/toast/toastService';

interface CharacterVisibilitySettingsProps {
  character: Character;
  isOpen: boolean;
  onClose: () => void;
  /** Display-only now — see the AUTHORIZATION note above. */
  isGM?: boolean;
  /** Gates whether Save/checkboxes are editable. Defaults `true` because the
   * only existing call site only ever opens this for the viewer's own
   * character; pass `false` explicitly for a genuine read-only viewer. */
  isOwner?: boolean;
}

// Visibility field labels
const VISIBILITY_FIELDS = [
  { key: 'hp', label: 'Hit Points' },
  { key: 'armor_class', label: 'Armor Class' },
  { key: 'speed', label: 'Speed' },
  { key: 'strength', label: 'Strength' },
  { key: 'dexterity', label: 'Dexterity' },
  { key: 'constitution', label: 'Constitution' },
  { key: 'intelligence', label: 'Intelligence' },
  { key: 'wisdom', label: 'Wisdom' },
  { key: 'charisma', label: 'Charisma' },
  { key: 'skills', label: 'Skills' },
  { key: 'spells', label: 'Spells' },
  { key: 'equipment', label: 'Equipment' }
];

export const CharacterVisibilitySettings: React.FC<CharacterVisibilitySettingsProps> = ({
  character,
  isOpen,
  onClose,
  isGM = false,
  isOwner = true
}) => {
  const [visibilitySettings, setVisibilitySettings] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize visibility settings from character. The backend's default is
  // "all visible" for a field never explicitly set — an OMITTED key in
  // character.visibility must display as checked (true), not as an
  // unchecked/false checkbox, or this UI would show fields as hidden that
  // the backend actually treats as visible.
  useEffect(() => {
    if (isOpen && character) {
      const withDefaults: Record<string, boolean> = {};
      for (const field of VISIBILITY_FIELDS) {
        const stored = character.visibility[field.key as keyof typeof character.visibility];
        withDefaults[field.key] = stored ?? true;
      }
      setVisibilitySettings(withDefaults);
      setError(null);
    }
  }, [isOpen, character]);

  const handleToggle = (field: string) => {
    if (!isOwner) return; // read-only for a non-owner viewer

    setVisibilitySettings(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      await characterService.updateCharacterVisibility(
        character.character_id,
        visibilitySettings
      );
      
      toastService.success('Visibility settings updated');
      
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update visibility';
      setError(message);
      toastService.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1a1a1a',
          border: '2px solid #d4af37',
          borderRadius: '12px',
          padding: '32px',
          minWidth: '400px',
          maxWidth: '500px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
      >
        {/* Header */}
        <h2 style={{
          margin: 0,
          marginBottom: '8px',
          fontSize: '24px',
          color: '#d4af37',
          fontWeight: 'bold'
        }}>
          Character Visibility
        </h2>
        <div style={{
          fontSize: '14px',
          color: '#888',
          marginBottom: '24px'
        }}>
          {character.character_name} {isGM ? '(Viewing as GM)' : '(Your Character)'}
        </div>

        {/* Visibility Checkboxes */}
        <div style={{ marginBottom: '24px' }}>
          {VISIBILITY_FIELDS.map((field) => (
            <div
              key={field.key}
              onClick={() => handleToggle(field.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                backgroundColor: '#2a2a2a',
                border: '1px solid #444',
                borderRadius: '8px',
                marginBottom: '8px',
                cursor: isOwner ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                opacity: isOwner ? 1 : 0.7
              }}
              onMouseEnter={(e) => {
                if (isOwner) e.currentTarget.style.backgroundColor = '#333';
              }}
              onMouseLeave={(e) => {
                if (isOwner) e.currentTarget.style.backgroundColor = '#2a2a2a';
              }}
            >
              {/* Checkbox */}
              <div style={{
                width: '24px',
                height: '24px',
                border: `2px solid ${visibilitySettings[field.key] ? '#d4af37' : '#666'}`,
                borderRadius: '4px',
                backgroundColor: visibilitySettings[field.key] ? '#d4af37' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#1a1a1a',
                fontSize: '16px',
                fontWeight: 'bold',
                flexShrink: 0
              }}>
                {visibilitySettings[field.key] && '✓'}
              </div>

              {/* Label */}
              <div style={{
                fontSize: '15px',
                color: visibilitySettings[field.key] ? '#d4af37' : '#aaa',
                fontWeight: '500'
              }}>
                {field.label}
              </div>
            </div>
          ))}
        </div>

        {/* Informational Note */}
        <div style={{
          padding: '12px',
          backgroundColor: '#2a2a2a',
          border: '1px solid #444',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#888',
          marginBottom: '24px',
          fontStyle: 'italic'
        }}>
          <strong style={{ color: '#d4af37' }}>Note:</strong> Unchecked fields are hidden from every other campaign member, including the Game Maker — the GM does not automatically see hidden fields (PRD001A 001A.4; this was a stale claim in an earlier version of this note).
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#ffe0e0',
            border: '1px solid #c41e3a',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            color: '#c41e3a'
          }}>
            {error}
          </div>
        )}

        {/* Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              padding: '12px 24px',
              fontSize: '15px',
              fontWeight: '600',
              backgroundColor: 'transparent',
              color: '#aaa',
              border: '2px solid #444',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Cancel
          </button>
          {isOwner && (
            <button
              type="button"
              onClick={handleSave}
              disabled={isSubmitting}
              style={{
                padding: '12px 28px',
                fontSize: '15px',
                fontWeight: '600',
                backgroundColor: isSubmitting ? '#666' : '#d4af37',
                color: '#1a1a1a',
                border: 'none',
                borderRadius: '8px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

